export interface VolumeDiscountTier {
    orderValueMin: number;
    discountPercent: number;
    active: boolean;
}

export function parseVolumeDiscounts(input: unknown): VolumeDiscountTier[] {
    if (input == null) return [];
    let raw: unknown = input;
    if (typeof input === "string") {
        try {
            raw = JSON.parse(input);
        } catch {
            return [];
        }
    }
    if (!Array.isArray(raw)) return [];

    const tiers: VolumeDiscountTier[] = [];
    for (const item of raw) {
        if (!item || typeof item !== "object") continue;
        const orderValueMin = Number((item as VolumeDiscountTier).orderValueMin);
        const discountPercent = Number(
            (item as VolumeDiscountTier).discountPercent
        );
        if (!Number.isFinite(orderValueMin) || orderValueMin < 0) continue;
        if (!Number.isFinite(discountPercent) || discountPercent < 0) continue;
        tiers.push({
            orderValueMin: Math.round(orderValueMin),
            discountPercent: Math.round(discountPercent * 100) / 100,
            active: (item as VolumeDiscountTier).active !== false,
        });
    }
    return tiers.sort((a, b) => a.orderValueMin - b.orderValueMin);
}

/** Active tiers that offer a real discount (for display tables). */
export function getDisplayVolumeDiscountTiers(
    input: unknown
): VolumeDiscountTier[] {
    return parseVolumeDiscounts(input).filter(
        (tier) => tier.active && tier.discountPercent > 0
    );
}

/**
 * Pick the best active tier for a channel/pricelist order value.
 * `orderValueMin` is whole currency units; `orderValueCents` is Vendure money.
 */
export function selectVolumeDiscountTier(
    orderValueCents: number,
    tiers: VolumeDiscountTier[]
): VolumeDiscountTier | null {
    if (!tiers.length || orderValueCents < 0) return null;

    let best: VolumeDiscountTier | null = null;
    for (const tier of tiers) {
        if (!tier.active) continue;
        if (tier.orderValueMin * 100 > orderValueCents) continue;
        if (!best || tier.orderValueMin > best.orderValueMin) {
            best = tier;
        }
    }
    return best;
}


export function inferAppliedVolumeDiscount(
    orderValueCents: number,
    input: unknown
): VolumeDiscountTier | null {
    const tiers = parseVolumeDiscounts(input);
    if (!tiers.length || orderValueCents <= 0) return null;

    let best: VolumeDiscountTier | null = null;
    for (const tier of tiers) {
        if (!tier.active || !(tier.discountPercent > 0)) continue;
        const factor = 1 - tier.discountPercent / 100;
        if (factor <= 0) continue;

        const estimatedPreCents = Math.round(orderValueCents / factor);
        const expectedPostCents = Math.round(estimatedPreCents * factor);
       
        if (Math.abs(expectedPostCents - orderValueCents) > 1) continue;
        if (Math.abs(estimatedPreCents - orderValueCents) <= 1) continue;

        const selected = selectVolumeDiscountTier(estimatedPreCents, tiers);
        if (
            !selected ||
            selected.orderValueMin !== tier.orderValueMin ||
            selected.discountPercent !== tier.discountPercent
        ) {
            continue;
        }
        if (!best || tier.orderValueMin > best.orderValueMin) {
            best = tier;
        }
    }
    return best;
}

export function formatVolumeDiscountTierLabel(tier: VolumeDiscountTier): string {
    const amount = `€${tier.orderValueMin.toLocaleString()}`;
    return `From ${amount} — ${tier.discountPercent}%`;
}

export function getVolumeDiscountTeaser(input: unknown): string | null {
    const tiers = getDisplayVolumeDiscountTiers(input);
    if (!tiers.length) return null;
    const lowest = tiers[0];
    return `Volume discounts from €${lowest.orderValueMin.toLocaleString()}`;
}
